import { useState } from "react";
import { useSlidingPill } from "@/components/shared/Pill";
import { Search, SlidersHorizontal, X } from "lucide-react";

// Segment (pohledy / rozsah) — kapsle drží popisek i počet na jednom řádku.
// 383: vybraná záložka má posuvný podklad s pružinou (useSlidingPill); než se změří, drží barvu tlačítko.
export function Segment({ tabs, value, onChange, testid, compact = false }) {
  const sp = useSlidingPill(value, tabs.map((t) => `${t.label}${t.count ?? ""}`).join("|"));
  return (
    <div ref={sp.ref} className="relative flex h-11 p-1 bg-card pp-tint border border-border rounded-full max-w-full overflow-x-auto no-scrollbar" data-testid={testid}>
      {sp.pill("bg-brand shadow-sm")}
      {tabs.map((t) => {
        const on = value === t.value;
        return (
          <button key={t.value} type="button" onClick={() => onChange(t.value)} data-testid={t.testid} data-pill-on={on || undefined}
            className={`relative z-[1] h-full ${compact ? "px-2.5 md:px-3.5" : "px-5"} rounded-full text-[14.5px] inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 transition-colors ${on ? `${sp.ready ? "" : "bg-brand shadow-sm"} text-white font-bold` : "font-semibold text-muted-foreground hover:text-foreground"}`}>
            {t.icon ? <t.icon size={16} /> : null}{t.label}
            {t.count != null && t.count !== 0 && (
              <span className={`rounded-full px-[7px] py-px text-[12.5px] font-bold tabular-nums ${on ? "bg-white/25" : "bg-secondary text-muted-foreground"}`}>{t.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Jednotný pruh filtrování (zadání 108/§1): segmenty · Filtry · hledání + řádka
// aktivních chipů. Pozadí/okraj shodné se sloupci obsahu, radius 26 (ne kapsle,
// ať se při zúžení pruh zlomí, nezakulatí do „blobu").
export function FilterBar({ segments = [], middle, filterCount = 0, onFilterClick, filterBtnRef,
  search, onSearch, searchPlaceholder = "Hledat…", chips = [], onClearAll, resultLine, trailing, testid = "filter-bar",
  mobileIcons = false }) {
  const active = filterCount > 0;
  // mobileIcons (176d): na mobilu jen ikony — Filtry = posuvníky, hledání = lupa,
  // která se po klepnutí rozbalí na celý řádek. PC a tablet beze změny.
  const [searchOpen, setSearchOpen] = useState(false);
  const showSearch = !mobileIcons || searchOpen || !!search;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        {segments.map((s, i) => <Segment key={i} {...s} />)}
        {onFilterClick && (
          <button type="button" ref={filterBtnRef} onClick={onFilterClick} data-testid="filters-btn"
            aria-label="Filtry"
            className={`relative h-11 px-4 gap-2 bg-card rounded-full font-bold box-border inline-flex items-center border transition-colors ${mobileIcons ? "max-md:w-11 max-md:px-0 max-md:justify-center" : ""} ${active ? "border-brand text-brand" : "border-border text-foreground hover:text-brand"}`}>
            <SlidersHorizontal size={16} /> <span className={mobileIcons ? "max-md:hidden" : ""}>Filtry</span>
            {active && <span className={`rounded-full bg-brand text-white px-2 text-[12.5px] font-bold tabular-nums ${mobileIcons ? "max-md:absolute max-md:-top-1 max-md:-right-1 max-md:px-1.5 max-md:text-[11px]" : ""}`} data-testid="filters-btn-count">{filterCount}</span>}
          </button>
        )}
        {/* Volitelný rychlý ovladač mezi segmentem a hledáním (např. roll-in Fáze) */}
        {middle}
        {onSearch && mobileIcons && !showSearch && (
          <button type="button" onClick={() => setSearchOpen(true)} aria-label="Hledat" data-testid="filter-search-open"
            className="md:hidden h-11 w-11 bg-card rounded-full border border-border inline-flex items-center justify-center text-foreground hover:text-brand">
            <Search size={17} />
          </button>
        )}
        {onSearch ? (
          // max-w drží hledání krátké, ať se lišta vejde na obrazovku i s trailing
          // blokem (PC i tablet); zbylé místo vyplní ml-auto na trailing níže.
          <div className={`flex-1 min-w-[160px] max-w-[440px] h-11 bg-card border border-border rounded-full px-4 items-center gap-2 ${
            !mobileIcons ? "inline-flex" : showSearch ? "inline-flex max-md:order-last max-md:basis-full max-md:max-w-none" : "hidden md:inline-flex"}`}>
            <Search size={16} className="text-muted-foreground shrink-0" />
            <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} autoFocus={mobileIcons && searchOpen}
              data-testid="filter-search" className="bg-transparent focus:outline-none w-full text-sm" />
            {mobileIcons && searchOpen && (
              <button type="button" onClick={() => { onSearch(""); setSearchOpen(false); }} aria-label="Zavřít hledání" data-testid="filter-search-close"
                className="md:hidden -mr-1 w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground shrink-0"><X size={16} /></button>
            )}
          </div>
        ) : (
          <div className="flex-1" />
        )}
        {trailing && <div className="ml-auto min-w-0 flex items-center">{trailing}</div>}
      </div>

      {chips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap px-1.5" data-testid="active-filters">
          <span className="text-[13px] text-muted-foreground">Filtruje se:</span>
          {chips.map((c) => (
            <span key={c.key} data-testid={c.testid}
              className="h-[34px] pl-3 pr-2 bg-card border border-border rounded-full text-[13.5px] font-semibold inline-flex items-center gap-1.5">
              {c.label}
              <button type="button" onClick={c.clear} aria-label="Zrušit filtr" data-testid={`${c.testid}-clear`}
                className="text-muted-foreground hover:text-destructive text-base leading-none">×</button>
            </span>
          ))}
          <button type="button" onClick={onClearAll} data-testid="filter-clear-all"
            className="text-[13px] font-semibold text-brand underline">zrušit vše</button>
          {resultLine && <span className="ml-auto text-[13px] text-muted-foreground tabular-nums whitespace-nowrap" data-testid="filter-result-count">{resultLine}</span>}
        </div>
      )}
    </div>
  );
}
